/**
 * Client-side image normalization (#6130, wired into every photo slot by #14350
 * through photo_crop_controller; profile_photo_controller uses it directly).
 *
 * iPhone Safari uploads photos as HEIC/HEIF by default, and only Safari can
 * DISPLAY a HEIC. iPhone Safari CAN decode HEIC to a <canvas>, so we re-encode
 * HEIC (and any other image) to JPEG in the browser BEFORE upload — HEIC never
 * reaches the server on the primary (iPhone) path. Desktop Chrome/Firefox
 * cannot decode HEIC, so normalization is best-effort there, and the server
 * refuses the raw upload (app/lib/heic_upload.rb). The app servers themselves
 * do decode HEIC since libheif 1.19 (#6141), so a HEIC already stored gets a
 * normal server-side variant (#14800).
 */

// Content types / extensions that only Safari can display and that browsers may
// hand us from an iPhone.
const HEIC_TYPES = ["image/heic", "image/heif"]
const HEIC_EXTENSION = /\.hei[cf]$/i

const JPEG_TYPE = "image/jpeg"
const DEFAULT_QUALITY = 0.9

/**
 * Shown when a HEIC could not be decoded here at all (desktop Chrome/Firefox):
 * names the fix rather than the bare type rule. The server refuses the same
 * upload with the same wording (HeicUpload::REFUSAL) should it get that far —
 * it never does from a browser that can decode HEIC (#14350).
 */
export const HEIC_UNSUPPORTED_MESSAGE =
  "This browser could not convert your iPhone HEIC photo — try Safari on your iPhone, or upload a JPG or PNG."

/**
 * True when the File looks like HEIC/HEIF by MIME type or extension.
 * Some browsers report an empty `type` for HEIC, so we also check the name.
 */
export function isHeic(file) {
  if (!file) return false
  const type = (file.type || "").toLowerCase()
  if (HEIC_TYPES.includes(type)) return true
  return HEIC_EXTENSION.test(file.name || "")
}

/**
 * Re-encode an image File to JPEG via a <canvas>. Resolves with a new JPEG File
 * on success. If the browser cannot decode the source (e.g. desktop Chrome with
 * HEIC), resolves with the ORIGINAL file so the caller's flow is unchanged and
 * the server fallback can handle it.
 *
 * @param {File} file
 * @param {Object} [opts]
 * @param {number} [opts.quality=0.9] JPEG quality (0–1)
 * @param {number} [opts.maxDimension] optional max width/height in px (keeps aspect ratio)
 * @returns {Promise<File>}
 */
export function normalizeImageToJpeg(file, opts = {}) {
  const quality = typeof opts.quality === "number" ? opts.quality : DEFAULT_QUALITY
  const maxDimension = opts.maxDimension

  return new Promise((resolve) => {
    if (!file) {
      resolve(file)
      return
    }

    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    const cleanup = () => URL.revokeObjectURL(objectUrl)

    img.onload = () => {
      try {
        let { width, height } = img
        if (maxDimension && (width > maxDimension || height > maxDimension)) {
          const scale = maxDimension / Math.max(width, height)
          width = Math.round(width * scale)
          height = Math.round(height * scale)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            cleanup()
            if (!blob) {
              resolve(file)
              return
            }
            resolve(new File([blob], jpegFilename(file.name), { type: JPEG_TYPE }))
          },
          JPEG_TYPE,
          quality,
        )
      } catch {
        cleanup()
        resolve(file)
      }
    }

    img.onerror = () => {
      // Browser can't decode this format (e.g. HEIC on desktop). Fall back to
      // the original file; the server-side guard covers it.
      cleanup()
      resolve(file)
    }

    img.src = objectUrl
  })
}

/**
 * Normalize only when needed: HEIC/HEIF is always re-encoded to JPEG; other
 * images pass through untouched. Resolves with a File (new or original).
 */
export function normalizeIfHeic(file, opts = {}) {
  if (!isHeic(file)) return Promise.resolve(file)
  return normalizeImageToJpeg(file, opts)
}

function jpegFilename(name) {
  if (!name) return "photo.jpg"
  return name.replace(/\.[^.]+$/, "") + ".jpg"
}
